import React, { useEffect, useMemo, useState } from "react";
import { getApiUrl } from "../utils/api";

const fallbackVideo = {
  title: "Event Overview",
  description: "Watch our event overview on YouTube.",
  youtube_url: ""
};

const resolveVideoUrl = (value) => {
  if (!value || typeof value !== "string") return "";
  if (value.startsWith("http://") || value.startsWith("https://") || value.startsWith("data:") || value.startsWith("blob:")) {
    return value;
  }
  if (value.startsWith("/")) return getApiUrl(value);
  return `/${value}`;
};

const extractYoutubeId = (url) => {
  if (!url) return "";

  const cleaned = String(url).trim();
  const shortMatch = cleaned.match(/youtu\.be\/([^?&/]+)/i);
  if (shortMatch) return shortMatch[1];

  const watchMatch = cleaned.match(/[?&]v=([^?&/]+)/i);
  if (watchMatch) return watchMatch[1];

  const embedMatch = cleaned.match(/youtube\.com\/embed\/([^?&/]+)/i);
  if (embedMatch) return embedMatch[1];

  return "";
};

const isYoutubeUrl = (value) => Boolean(extractYoutubeId(value));

const resolveOverviewVideo = (data) => {
  const youtubeUrl = data?.youtube_url || "";
  const hostedUrl = data?.video_url || data?.video || data?.file || data?.media || "";
  const embedSource = isYoutubeUrl(youtubeUrl) ? youtubeUrl : isYoutubeUrl(hostedUrl) ? hostedUrl : "";
  const directSource = !isYoutubeUrl(hostedUrl) && hostedUrl ? hostedUrl : !isYoutubeUrl(youtubeUrl) ? youtubeUrl : "";

  return {
    youtube_url: embedSource,
    video_url: resolveVideoUrl(directSource)
  };
};

function EventOverviewVideoSection({ className = "section section-tight" }) {
  const [video, setVideo] = useState(fallbackVideo);
  const [isDeleted, setIsDeleted] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadVideo = async () => {
      try {
        const response = await fetch(getApiUrl("/api/event-overview-video/"));
        if (!response.ok) throw new Error("No event overview video found");
        const data = await response.json();
        if (data?.deleted) {
          if (isMounted) setIsDeleted(true);
          return;
        }
        const resolved = resolveOverviewVideo(data);
        if (isMounted && (resolved.youtube_url || resolved.video_url)) {
          setVideo({
            title: data.title || fallbackVideo.title,
            description: data.description || "",
            youtube_url: resolved.youtube_url,
            video_url: resolved.video_url
          });
        }
      } catch (error) {
        if (isMounted) setVideo(fallbackVideo);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadVideo();
    return () => {
      isMounted = false;
    };
  }, []);

  const embedUrl = useMemo(() => {
    const youtubeId = extractYoutubeId(video?.youtube_url);
    return youtubeId ? `https://www.youtube.com/embed/${youtubeId}` : "";
  }, [video]);

  const directVideoUrl = useMemo(() => resolveVideoUrl(video?.video_url), [video]);

  if (!loading && isDeleted) return null;

  return (
    <section className={className}>
      <div className="container">
        <div className="film-card">
          <div className="row align-items-center gy-4 mb-3">
            <div className="col-lg-8">
              <h2>Event Overview Video</h2>
              <p className="section-copy mb-0">
                {video?.title || fallbackVideo.title}
              </p>
              {(video?.description || fallbackVideo.description) && (
                <p className="text-muted mt-2 mb-0">{video?.description || fallbackVideo.description}</p>
              )}
            </div>
          </div>
          {loading && <p className="text-muted mb-3">Loading video...</p>}
          {directVideoUrl ? (
            <div className="ratio ratio-16x9 rounded overflow-hidden">
              <video
                title="Event overview video"
                src={directVideoUrl}
                controls
                playsInline
                preload="metadata"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>
          ) : embedUrl ? (
            <div className="ratio ratio-16x9 rounded overflow-hidden">
              <iframe
                title="Event overview video"
                src={embedUrl}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              ></iframe>
            </div>
          ) : (
            <p className="text-muted mb-0">No video available yet.</p>
          )}
        </div>
      </div>
    </section>
  );
}

export default EventOverviewVideoSection;
